import { REPORT_SECTION_TITLE } from '@/pages/CompanyReport/constants';
import { getPossessive } from '@/pages/CompanyReport/utils/grammar';
import type { CompanyReportSection } from '@/pages/CompanyReport/useSection';
import { SUPPORTED_LOCALES, type Locale } from '@/utils/locale';
import { SWS_HOST } from '@/constants';

export const getTitle = ({
  companyName,
  companyShortName,
  tickerSymbol,
  exchangeTicker,
  uniqueSymbol,
  section,
}: {
  companyName: string;
  companyShortName: string;
  tickerSymbol: string;
  exchangeTicker: string;
  uniqueSymbol: string;
  section: CompanyReportSection;
}) => {
  switch (section) {
    case 'summary':
      return `${companyShortName} (${exchangeTicker}) - Stock Price, News & Analysis`;
    case 'valuation':
      return `${companyShortName} (${uniqueSymbol}) Stock Valuation, Peer Comparison & Price Targets`;
    case 'future':
      return `${companyShortName} (${uniqueSymbol}) Stock Forecast & Analyst Predictions`;
    case 'past':
      return `${companyShortName} (${uniqueSymbol}) - Earnings & Revenue Performance`;
    case 'health':
      return `${companyShortName} (${tickerSymbol})  Balance Sheet & Financial Health Metrics`;
    case 'dividend':
      return `${companyShortName} (${exchangeTicker}) Dividend Yield, History and Growth`;
    case 'management':
      return `${companyName} (${tickerSymbol}) Leadership & Management Team Analysis`;
    case 'ownership':
      return `${companyName} Insider Trading & Ownership Structure`;
    case 'information':
      return `${companyName} (${tickerSymbol}) Company Information`;
    default:
      return `${companyName} (${tickerSymbol})`;
  }
};

export const getDescription = ({
  companyName,
  companyShortName,
  tickerSymbol,
  exchangeTicker,
  section,
  latestDividendYield,
  latestDividendYieldFormatted,
  dividendGrowing,
  dividendCovered,
  currentDividendPayoutRatioFormatted,
  isBank,
}: {
  companyName: string;
  companyShortName: string;
  tickerSymbol: string;
  exchangeTicker: string;
  section: CompanyReportSection;
  latestDividendYield: number | null;
  latestDividendYieldFormatted: string;
  dividendGrowing: boolean;
  dividendCovered: boolean;
  currentDividendPayoutRatioFormatted: string;
  isBank: boolean;
}) => {
  switch (section) {
    case 'summary':
      return `Research ${getPossessive(
        companyShortName
      )} (${exchangeTicker}) stock price, latest news & stock analysis. Find everything from its Valuation, Future Growth, Past Performance and more.`;
    case 'valuation':
      return `Research ${getPossessive(
        companyShortName
      )} (${exchangeTicker}) stock key valuation metrics while comparing it with its industry peers & market side by side.`;
    case 'future':
      return `Discover ${getPossessive(
        companyShortName
      )} earnings and revenue growth rates, forecasts, and the latest analyst predictions while comparing them to its industry peers.`;
    case 'past':
      return `Discover the latest data for ${companyShortName} financials. From earnings and revenue to ROE, net margins, growth rates and more. Subscribe for free today!`;
    case 'health':
      if (isBank) {
        return `Discover ${getPossessive(
          companyShortName
        )} balance sheet and financial health metrics. Everything from Total Assets, Equity, and Cash-on-Hand to Bad Loan Levels and Debt.`;
      }
      return `Discover ${getPossessive(
        companyShortName
      )} balance sheet and financial health metrics. From total debt, total equity, assets to cash-on-hand, interest coverage and more.`;
    case 'dividend':
      if (latestDividendYield === 0 || latestDividendYield === null) {
        return 'Company does not have a record of paying a dividend.';
      } else {
        return `${companyShortName} (${exchangeTicker}) dividend yield is ${latestDividendYieldFormatted}. Dividend payments have ${
          dividendGrowing ? 'increased' : 'decreased'
        } over the last 10 years and are ${
          dividendCovered ? 'covered' : 'not covered'
        } by earnings with a payout ratio of ${currentDividendPayoutRatioFormatted}.`;
      }
    case 'management':
      return `Learn about ${companyName} (${tickerSymbol}) stock's management team. Comprehensive performance, salary and tenure analysis for the CEO, board and leadership team.`;
    case 'ownership':
      return `Uncover the latest insider trading activity for ${companyName} (${tickerSymbol}). Know which insiders are buying and selling along with top shareholders and ownership breakdown.`;
    case 'information':
      return `Learn more about ${companyName} (${tickerSymbol}). From their employee growth over the years and their exchange listings.`;
    default:
      return `${companyName} (${tickerSymbol})`;
  }
};

export const getPartOf = (section: CompanyReportSection, url: string) => {
  const partOf = [
    `${url}`,
    `${url}/valuation`,
    `${url}/future`,
    `${url}/past`,
    `${url}/health`,
    `${url}/dividend`,
    `${url}/management`,
    `${url}/ownership`,
    `${url}/information`,
  ];
  return section === 'summary'
    ? partOf.slice(1, partOf.length)
    : partOf.filter((webPage) => !webPage.endsWith(section));
};

type GetSectionSchema = {
  section: CompanyReportSection;
  companyName: string;
  url: string;
  title: string;
  description: string;
};
export const getSectionSchema = ({
  section,
  companyName,
  url,
  title,
  description,
}: GetSectionSchema) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    specialty: 'Teaching retail investors how to research stocks',
    mainEntity: {
      '@type': 'CreativeWork',
      about: `The page contains information regarding the ${REPORT_SECTION_TITLE[section]} of ${companyName}`,
      url,
      description,
      audience: 'Retail Investors',
      author: {
        '@type': 'Organization',
        name: 'Simply Wall St Pty Ltd.',
      },
      dateModified: '2023-08-25T00:00:00.000Z',
      headline: title,
      isAccessibleForFree: true,
      isPartOf: getPartOf(section, url),
    },
  };
};

const createHref = (
  canonicalURL: string,
  section: CompanyReportSection,
  locale?: Locale
) =>
  `${SWS_HOST}${locale === undefined ? '' : '/' + locale}${canonicalURL}${
    section === 'summary' ? '' : '/' + section
  }`;

export const getAlternates = (
  canonicalURL: string,
  section: CompanyReportSection
) => {
  const alternates: Array<{ lang: 'x-default' | Locale; href: string }> = [
    // Default alternate link should be the `en` locale, which is the base canonical URL.
    {
      lang: 'x-default',
      href: createHref(canonicalURL, section),
    },
    // Potentially a future issue, but supported locales used to create all alternates does not include `en` by default
    {
      lang: 'en',
      href: createHref(canonicalURL, section),
    },
  ];

  for (const l of SUPPORTED_LOCALES) {
    alternates.push({
      lang: l,
      href: createHref(canonicalURL, section, l),
    });
  }

  return alternates;
};
